/**
 * <Sparkline data={trend} dataKey="revenue" compareKey="previous_revenue" />
 *
 * Tiny dependency-free SVG line (cheap enough for every table row). Stretches to its container width.
 *   data        array of numbers, or of objects read with dataKey
 *   compareKey  optional second (dashed, grey) series from the same objects, e.g. the comparison period
 *   color       line colour (default blue), height px (default 32), fill true for a soft area
 *   label       if given the svg is announced as an image with this label; otherwise aria-hidden
 * Renders an empty box (same height) when there are fewer than 2 points.
 */
import React, { useId } from 'react';

const toPoints = (values, max, min, width, height) => {
  const span = max - min || 1;
  const step = values.length > 1 ? width / (values.length - 1) : width;
  return values.map((value, index) => [index * step, height - 2 - (((Number(value) || 0) - min) / span) * (height - 4)]);
};
const toPath = points => points.map(([x, y], index) => `${index ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');

export const Sparkline = ({ data = [], dataKey = 'value', compareKey, color = '#2563EB', height = 32, fill = true, label, className = '' }) => {
  const gradientId = `spark-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const read = key => (data || []).map(item => (typeof item === 'number' ? item : Number(item?.[key]) || 0));
  const values = read(dataKey);
  const compare = compareKey ? read(compareKey) : [];
  if (values.length < 2) return <div className={className} style={{ height }} aria-hidden="true" />;
  const all = [...values, ...compare];
  const max = Math.max(...all);
  const min = Math.min(0, ...all);
  const width = 100;
  const line = toPath(toPoints(values, max, min, width, height));
  return (
    <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" width="100%" height={height} className={className}
      role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true} focusable="false">
      {fill && (
        <defs>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.18" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
      )}
      {compare.length > 1 && (
        <path d={toPath(toPoints(compare, max, min, width, height))} fill="none" stroke="#94A3B8" strokeWidth="1.25" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
      )}
      {fill && <path d={`${line} L${width},${height} L0,${height} Z`} fill={`url(#${gradientId})`} stroke="none" />}
      <path d={line} fill="none" stroke={color} strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

export default Sparkline;
